import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useStatsigClient } from '@statsig/react-bindings';

import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import {
  QUESTION_GROUP_LOGIC,
  QUESTION_GROUP_LOGIC_BRANCHES,
  QUESTION_TYPES,
} from '@api/instant_survey';
import { GATES } from '@constants';
import { Announcement, Button, GPTIcon, OverviewIcon, toast } from '@utilities';

import {
  createQuestionKey,
  getNonInstructionQuestionCountBefore,
  getQuestionsWithScreenersFirst,
  hasInvalidSequentialMonadicQuestionLogicSources,
  isLanguageScreenerQuestion,
} from '../../../../utilities/helpers';
import {
  firstGroupSubtreeQuestionIndex,
  getPipingQuestionNumbersById,
  replacePipingMentionReferences,
} from '../../utilities';
import {
  buildRenderList,
  commitQuestionLogic,
  findGroupRenderItem,
  getAvailableParentGroupsByGroupId,
  getGroupIdFromDroppableId,
  getRenderItemQuestions,
  reorderQuestionsByRenderItems,
} from './utilities/helpers';

import ModalLogicChangeConfirmation from './components/ModalLogicChangeConfirmation';
import PreviewBanner from './components/PreviewBanner';
import Question from './components/Question';
import QuestionGroupBlock from './components/QuestionGroupBlock';
import QuestionPlaceholder from './components/QuestionPlaceholder';
import SequentialMonadicBlock, {
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX,
  SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE,
} from './components/SequentialMonadicBlock';
import { SurveyContext } from '../../../../utilities/context';
import QuestionnaireDisplay from '@src/app/pages/Dashboard/components/QuestionnaireDisplay';

import rocketError from './assets/rocket-error.svg';

import styles from './_index.module.scss';

const SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR =
  'Moving this question will break existing monadic question logic. Update or remove the logic before moving this question.';

const QuestionnaireList = ({
  calculateQuestionNumbers,
  canPreviewSurvey,
  firstErrorRef,
  generationError,
  generationErrorMessage,
  isDetailedQuestionView,
  isPreviewDisabled,
  isVideoSurvey,
  onAcceptPreview,
  onCancelPreview,
  onCreateQuestion,
  onDuplicateQuestion,
  onEditQuestion,
  onOpenAIGenerator,
  onPreviewSurvey,
  onRemoveQuestion,
  onReprompt,
  pipingQuestionsMap,
  previewDisabledTooltip,
  questions,
  showActions,
  showPreviewBanner,
}) => {
  const { isReadOnly, questionDisplayIndexById, setShowSurveyTemplateModal, survey, updateSurvey } =
    useContext(SurveyContext);
  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAdvancedEditSurvey, canUseAIGenerationQuestionnaire } = permissions;
  const { client } = useStatsigClient();
  const showQuestionGroupBlocks = client.checkGate(GATES.QUESTION_GROUP_BLOCKS);
  const [questionBeingMoved, setQuestionBeingMoved] = useState(null);
  const [questionsRequiringLogicConfirmation, setQuestionsRequiringLogicConfirmation] =
    useState(null);
  const sharedQuestionnaireDisplayProps = {
    canPreviewSurvey,
    firstErrorRef,
    isDetailedQuestionView,
    isPreviewDisabled,
    onPreviewSurvey,
    previewDisabledTooltip,
    questionDisplayIndexById,
    questionLogic: survey?.questionLogic,
    questions,
    selectedQuestions: questions,
  };

  // Build the render list. When the gate is off we render flat, so surveys that already carry
  // questionGroup data don't show group blocks to gate-off users.
  const questionGroups = survey?.questionGroups || {};
  const renderList = useMemo(
    () => buildRenderList(questions, questionGroups, { enableGroups: showQuestionGroupBlocks }),
    [questions, questionGroups, showQuestionGroupBlocks]
  );
  const availableParentGroupsByGroupId = useMemo(
    () => getAvailableParentGroupsByGroupId(questionGroups),
    [questionGroups]
  );

  const handleSetQuestionLogic = (questionLogic) =>
    commitQuestionLogic({ questionLogic, survey, updateSurvey });

  const getSequentialMonadicQuestionIdFromDroppableId = ({ droppableId = '' }) => {
    const prefix = `${SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX}-`;
    if (!droppableId.startsWith(prefix)) return null;
    return droppableId.slice(prefix.length);
  };

  const reorderSequentialMonadicSubQuestions = ({ destinationIndex, questionId, sourceIndex }) => {
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      if (String(surveyQuestion?.id) !== questionId) return surveyQuestion;

      const existingSubQuestions = surveyQuestion.subQuestions || [];
      const activeSubQuestions = existingSubQuestions.filter(({ _delete }) => !_delete);
      const deletedSubQuestions = existingSubQuestions.filter(({ _delete }) => _delete);
      const reorderedSubQuestions = [...activeSubQuestions];
      const [movedSubQuestion] = reorderedSubQuestions.splice(sourceIndex, 1);

      if (!movedSubQuestion) return surveyQuestion;

      reorderedSubQuestions.splice(destinationIndex, 0, movedSubQuestion);

      return {
        ...surveyQuestion,
        subQuestions: [...reorderedSubQuestions, ...deletedSubQuestions],
      };
    });

    if (
      hasInvalidSequentialMonadicQuestionLogicSources({
        questionLogic: survey?.questionLogic,
        questions: updatedQuestions,
      })
    ) {
      toast.error(SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR);
      return;
    }

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  const onDragEnd = (result) => {
    const destinationIndex = result?.destination?.index;
    const sourceIndex = result?.source?.index;

    if (
      !result?.destination ||
      result.source.droppableId !== result.destination.droppableId ||
      destinationIndex === sourceIndex
    ) {
      return;
    }

    if (result.type === SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE) {
      const questionId = getSequentialMonadicQuestionIdFromDroppableId({
        droppableId: result.source.droppableId,
      });
      if (!questionId) return;

      reorderSequentialMonadicSubQuestions({ destinationIndex, questionId, sourceIndex });
      return;
    }

    // Reorder within a group's inner droppable or within the main list. The drag indices point at
    // the rendered list (where a group is one block), so resolve the render items for whichever
    // droppable was dragged and translate the move back to the flat question list.
    const sourceGroupId = getGroupIdFromDroppableId(result.source.droppableId);
    const items =
      sourceGroupId != null ? findGroupRenderItem(renderList, sourceGroupId)?.items : renderList;
    if (!items) return;

    const sourceRenderItem = items[sourceIndex];
    if (!sourceRenderItem) return;

    // Use createQuestionKey (persisted id → questionClientId) so unsaved questions with no backend
    // id are distinct — matching on raw id would treat every id-less question as the same one.
    const movingKeys = new Set(getRenderItemQuestions(sourceRenderItem).map(createQuestionKey));
    const isMoving = (q) => movingKeys.has(createQuestionKey(q));
    const movingQuestions = survey.questions.filter(isMoving);
    let updatedQuestions = reorderQuestionsByRenderItems({
      destinationIndex,
      items,
      questions: survey.questions,
      sourceIndex,
    });
    updatedQuestions = getQuestionsWithScreenersFirst(updatedQuestions);

    // The language screener is pinned to the first position and can't be reordered,
    // so re-hoist it in case another question was dropped above it.
    const languageScreenerIndex = updatedQuestions.findIndex(
      (question) => !question._delete && isLanguageScreenerQuestion(question)
    );
    if (languageScreenerIndex > 0) {
      const [languageScreener] = updatedQuestions.splice(languageScreenerIndex, 1);
      updatedQuestions.unshift(languageScreener);
    }
    // Recalculate question numbers based on new order
    const updatedQuestionNumbers = calculateQuestionNumbers(updatedQuestions);
    const originalQuestionNumbers = calculateQuestionNumbers(survey.questions);
    const updatedPipingQuestionNumbers = getPipingQuestionNumbersById(updatedQuestions);
    const originalPipingQuestionNumbers = getPipingQuestionNumbersById(survey.questions);

    const hasCarryForwardQuestionChanges = updatedQuestions.some(({ id }) => {
      const carryForwardLogic = survey?.carryForwardLogic?.[id];
      if (!carryForwardLogic?.sourceSurveyQuestionId) return false;

      return (
        updatedQuestionNumbers[carryForwardLogic.sourceSurveyQuestionId] >
        updatedQuestionNumbers[id]
      );
    });

    if (hasCarryForwardQuestionChanges) {
      toast.error(
        'Moving this question will break existing carry forward logic. Update or remove the logic before moving this question.'
      );
      return;
    }

    if (
      hasInvalidSequentialMonadicQuestionLogicSources({
        questionLogic: survey?.questionLogic,
        questions: updatedQuestions,
      })
    ) {
      toast.error(SEQUENTIAL_MONADIC_LOGIC_REORDER_ERROR);
      return;
    }

    const hasLogicChanges = updatedQuestions.some(({ id }, index) => {
      const currentLogic = survey?.questionLogic[id];

      if (
        // only first two questions (ignoring Instructional Text) can disqualify
        currentLogic?.disqualify &&
        !canAdvancedEditSurvey &&
        getNonInstructionQuestionCountBefore(updatedQuestions, index) >= 2
      ) {
        return true;
      }

      if (
        currentLogic?.skip?.logic.some(
          // skip logic cannot reference questions that are after the current question
          ({ question }) =>
            question &&
            updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
        )
      ) {
        return true;
      }

      if (currentLogic?.option) {
        const hasInvalidOptionLogic = Object.values(currentLogic.option).some((answerOption) =>
          answerOption?.logic?.some(
            // option logic cannot reference questions that are after the current question
            ({ question }) =>
              question &&
              updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) > index
          )
        );
        return hasInvalidOptionLogic;
      }

      return false;
    });

    const questionGroupLogic = survey?.questionLogic?.[QUESTION_GROUP_LOGIC] || {};
    const hasGroupLogicChanges = Object.entries(questionGroupLogic).some(
      ([groupId, groupLogic]) => {
        const firstMemberIndex = firstGroupSubtreeQuestionIndex({
          groupId,
          questionGroups,
          questions: updatedQuestions,
        });
        if (firstMemberIndex === -1) return false;

        // Only a group starting within the first two questions (ignoring Instructional Text)
        // may disqualify, mirroring the per-question rule above.
        if (
          groupLogic.disqualify &&
          !canAdvancedEditSurvey &&
          getNonInstructionQuestionCountBefore(updatedQuestions, firstMemberIndex) >= 2
        ) {
          return true;
        }

        return QUESTION_GROUP_LOGIC_BRANCHES.some((branch) =>
          (groupLogic[branch]?.logic || []).some(
            ({ question }) =>
              question &&
              updatedQuestions.findIndex(({ id: questionId }) => question.id === questionId) >=
                firstMemberIndex
          )
        );
      }
    );

    const hasPipingQuestionChanges = updatedQuestions.some(({ id }) => {
      const children = pipingQuestionsMap.childrenByParent[id] || [];
      const hasChildrenAllAfterParent = children.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] > updatedPipingQuestionNumbers[id]
      );
      const parents = pipingQuestionsMap.parentsByChild[id] || [];
      const hasParentsAllBeforeChild = parents.every(
        ({ questionId }) =>
          updatedPipingQuestionNumbers[questionId] < updatedPipingQuestionNumbers[id]
      );

      if (!hasParentsAllBeforeChild || !hasChildrenAllAfterParent) {
        return true;
      }
      return false;
    });

    // A group block moves several questions at once, but the logic-confirmation modal resolves
    // piping against a single moved question. Rather than confirm/auto-fix only the first member,
    // block group moves that would break piping. (Skip/disqualify/option logic below scans the full
    // list, so it is still surfaced correctly for group moves.)
    if (movingQuestions.length > 1 && hasPipingQuestionChanges) {
      toast.error(
        'Moving this group will break existing piping logic. Update or remove the logic before moving this group.'
      );
      return;
    }

    const questionsWithResyncedPiping = updatedQuestions.map((question) => {
      const parents = pipingQuestionsMap.parentsByChild[question.id] || [];
      if (!parents.length) {
        return question;
      }

      const mentionReferenceMappings = parents.reduce((acc, { questionId: parentId }) => {
        const oldParentCanonicalQuestionNumber = originalPipingQuestionNumbers[parentId];
        const newParentCanonicalQuestionNumber = updatedPipingQuestionNumbers[parentId];
        const oldParentDisplayQuestionNumber = originalQuestionNumbers[parentId];
        const newParentDisplayQuestionNumber = updatedQuestionNumbers[parentId];

        if (
          !oldParentCanonicalQuestionNumber ||
          !newParentCanonicalQuestionNumber ||
          !oldParentDisplayQuestionNumber ||
          !newParentDisplayQuestionNumber
        ) {
          return acc;
        }

        acc.push({
          newCanonicalQuestionNumber: newParentCanonicalQuestionNumber,
          newDisplayQuestionNumber: newParentDisplayQuestionNumber,
          oldCanonicalQuestionNumber: oldParentCanonicalQuestionNumber,
          oldDisplayQuestionNumber: oldParentDisplayQuestionNumber,
        });
        return acc;
      }, []);

      const nextRichText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.richText,
      });

      const nextText = replacePipingMentionReferences({
        mappings: mentionReferenceMappings,
        value: question.text,
      });

      let hasAnswerChanges = false;
      const nextAnswers = (question.answers || []).map((answer) => {
        const nextAnswerRichText = replacePipingMentionReferences({
          mappings: mentionReferenceMappings,
          value: answer.richText,
        });
        const nextAnswerText = replacePipingMentionReferences({
          mappings: mentionReferenceMappings,
          value: answer.text,
        });

        if (nextAnswerRichText === answer.richText && nextAnswerText === answer.text) {
          return answer;
        }

        hasAnswerChanges = true;

        return {
          ...answer,
          richText: nextAnswerRichText,
          text: nextAnswerText,
        };
      });

      if (nextRichText === question.richText && nextText === question.text && !hasAnswerChanges) {
        return question;
      }

      return {
        ...question,
        answers: nextAnswers,
        richText: nextRichText,
        text: nextText,
      };
    });

    if (hasLogicChanges || hasGroupLogicChanges || hasPipingQuestionChanges) {
      setQuestionBeingMoved(movingQuestions[0]);
      setQuestionsRequiringLogicConfirmation(questionsWithResyncedPiping);
    } else {
      updateSurvey({ questions: questionsWithResyncedPiping }, { markDirty: true });
    }
  };

  return (
    <>
      <div className={styles['questionnaire-list-container']}>
        {questions.length === 0 &&
          !isVideoSurvey &&
          canUseAIGenerationQuestionnaire &&
          !isReadOnly &&
          !generationError && (
            <Announcement
              className={styles['questionnaire-list-announcement']}
              elevation={1}
              header="Introducing AI Generated Questionnaires"
              icon={<GPTIcon />}
              text={
                <>
                  Writer&apos;s block? Get started with AI suggested questions. Try an{' '}
                  <span
                    className={styles['questionnaire-list-banner-link']}
                    onClick={onOpenAIGenerator}
                  >
                    AI Generated Questionnaire
                  </span>{' '}
                  now.
                </>
              }
              variant="ai"
            />
          )}
        {!isVideoSurvey && questions.length === 0 && !generationError && !isReadOnly && (
          <Announcement
            className={styles['questionnaire-list-announcement']}
            elevation={1}
            header="Questionnaire Templates"
            icon={<OverviewIcon />}
            text={
              <>
                Use one of our{' '}
                <span
                  className={styles['questionnaire-list-banner-link']}
                  onClick={() => setShowSurveyTemplateModal(true)}
                >
                  Questionnaire Templates
                </span>{' '}
                to quickly get started.
              </>
            }
            variant="info"
          />
        )}
        {(showPreviewBanner || generationError) && (
          <PreviewBanner
            generationError={generationError}
            generationErrorMessage={generationErrorMessage}
            onAcceptPreview={onAcceptPreview}
            onCancelPreview={onCancelPreview}
            onReprompt={onReprompt}
          />
        )}
        {generationError && !showPreviewBanner && (
          <div className={styles['questionnaire-list-ai-error']}>
            <img alt="Error" src={rocketError} />
            <p className={styles['questionnaire-list-ai-error-message']}>
              Houston, we have a problem
            </p>
            {generationErrorMessage && (
              <p className={styles['questionnaire-list-ai-error-detail']}>
                {generationErrorMessage}
              </p>
            )}
            {onReprompt && <Button onClick={onReprompt} text="Try Again" variant="ai" />}
          </div>
        )}
        {questions.length === 0 && !generationError && !isReadOnly && (
          <QuestionPlaceholder onCreateQuestion={onCreateQuestion} />
        )}
        {questions.length > 0 && !generationError && !showQuestionGroupBlocks && isReadOnly && (
          <QuestionnaireDisplay {...sharedQuestionnaireDisplayProps} mode="read" />
        )}
        {questions.length > 0 && !generationError && !showQuestionGroupBlocks && !isReadOnly && (
          <QuestionnaireDisplay
            {...sharedQuestionnaireDisplayProps}
            mode="edit"
            onDragEnd={onDragEnd}
            onDuplicateQuestion={onDuplicateQuestion}
            onEditQuestion={onEditQuestion}
            onRemoveQuestion={onRemoveQuestion}
            pipingQuestionsMap={pipingQuestionsMap}
            setQuestionLogic={(questionLogic) =>
              updateSurvey({ questionLogic }, { markDirty: true })
            }
            showActions={showActions}
          />
        )}
        {questions.length > 0 && !generationError && showQuestionGroupBlocks && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <div
                  className={styles['selections']}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {renderList.map((item, index) => {
                    // A group render-item is dragged as a single block; its inner questions
                    // are reordered by the group's own droppable inside QuestionGroupBlock.
                    if (item.type === 'group') {
                      const group = questionGroups[item.groupId] ?? {
                        id: item.groupId,
                        name: '',
                        randomizeOrder: false,
                      };

                      return (
                        <Draggable
                          draggableId={`question-group-block-${item.groupId}`}
                          index={index}
                          isDragDisabled={isReadOnly}
                          key={`question-group-block-${item.groupId}`}
                        >
                          {(provided) => (
                            <QuestionGroupBlock
                              availableParentGroupsByGroupId={availableParentGroupsByGroupId}
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              group={group}
                              isDetailedQuestionView={isDetailedQuestionView}
                              isPreviewDisabled={isPreviewDisabled}
                              items={item.items}
                              onDuplicateQuestion={onDuplicateQuestion}
                              onEditQuestion={onEditQuestion}
                              onPreviewSurvey={onPreviewSurvey}
                              onRemoveQuestion={onRemoveQuestion}
                              previewDisabledTooltip={previewDisabledTooltip}
                              provided={provided}
                              questionLogic={survey?.questionLogic}
                              questions={item.questions}
                              selectedQuestions={questions}
                              setQuestionLogic={handleSetQuestionLogic}
                              showActions={showActions}
                            />
                          )}
                        </Draggable>
                      );
                    }

                    const { flatIndex, question } = item;
                    // Use stable question.id for draggableId to prevent state bugs when reordering questions
                    const draggableId = `question-${
                      question.id ?? question.questionClientId ?? flatIndex
                    }`;
                    return (
                      <Draggable
                        draggableId={draggableId}
                        index={index}
                        isDragDisabled={isReadOnly || isLanguageScreenerQuestion(question)}
                        key={draggableId}
                      >
                        {(provided) => {
                          return question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ? (
                            <Question
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              // Question uses index as the flat position into survey.questions
                              index={flatIndex}
                              isDetailedQuestionView={isDetailedQuestionView}
                              isPreviewDisabled={isPreviewDisabled}
                              key={draggableId}
                              // String(flatIndex + 1) covers ai generated questions which will not have a calculated display index
                              // this works so long as generated questions are limited to single cost questions
                              number={
                                questionDisplayIndexById[question.id] ?? String(flatIndex + 1)
                              }
                              onDuplicateQuestion={onDuplicateQuestion}
                              onEditQuestion={onEditQuestion}
                              onPreviewSurvey={onPreviewSurvey}
                              onRemoveQuestion={onRemoveQuestion}
                              previewDisabledTooltip={previewDisabledTooltip}
                              provided={provided}
                              question={question}
                              questionLogic={survey?.questionLogic}
                              selectedQuestions={questions}
                              setQuestionLogic={handleSetQuestionLogic}
                              showActions={showActions}
                            />
                          ) : (
                            <SequentialMonadicBlock
                              canPreviewSurvey={canPreviewSurvey}
                              firstErrorRef={firstErrorRef}
                              isDetailedQuestionView={isDetailedQuestionView}
                              isPreviewDisabled={isPreviewDisabled}
                              onPreviewSurvey={onPreviewSurvey}
                              onRemoveQuestion={onRemoveQuestion}
                              pipingQuestionsMap={pipingQuestionsMap}
                              previewDisabledTooltip={previewDisabledTooltip}
                              provided={provided}
                              question={question}
                              questionLogic={survey?.questionLogic}
                              selectedQuestions={questions}
                              setQuestionLogic={handleSetQuestionLogic}
                              showActions={showActions}
                            />
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
      {questionsRequiringLogicConfirmation && (
        <ModalLogicChangeConfirmation
          calculateQuestionNumbers={calculateQuestionNumbers}
          onClose={() => setQuestionsRequiringLogicConfirmation(null)}
          pipingQuestionsMap={pipingQuestionsMap}
          questionBeingMoved={questionBeingMoved}
          questionGroups={survey?.questionGroups}
          questionLogic={survey?.questionLogic}
          questions={questionsRequiringLogicConfirmation}
          updateSurvey={updateSurvey}
        />
      )}
    </>
  );
};

QuestionnaireList.propTypes = {
  calculateQuestionNumbers: PropTypes.func.isRequired,
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  generationError: PropTypes.bool,
  generationErrorMessage: PropTypes.string,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isPreviewDisabled: PropTypes.bool,
  isVideoSurvey: PropTypes.bool.isRequired,
  onAcceptPreview: PropTypes.func,
  onCancelPreview: PropTypes.func,
  onCreateQuestion: PropTypes.func.isRequired,
  onDuplicateQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onOpenAIGenerator: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  onReprompt: PropTypes.func,
  pipingQuestionsMap: PropTypes.object.isRequired,
  previewDisabledTooltip: PropTypes.string,
  questions: PropTypes.array,
  showActions: PropTypes.bool,
  showPreviewBanner: PropTypes.bool,
};

export default QuestionnaireList;
